import JSZip from 'jszip'
import localforage from 'localforage'

import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, onScopeDispose, ref, watch } from 'vue'

import { sha256 } from '../utils/crypto'
import { showError, showSuccess } from '../utils/toast'

// ============================================================
// 百度网盘 OAuth Authorization Code 模式配置
// ⚠️ 安全隐患：client_secret 暴露在前端源码中，应迁移到服务端代理完成 token 交换
// ============================================================
const BAIDU_APP_KEY = '6EyGHGp94SWSFuocmg8UIJltY9uJnGiV'
const BAIDU_SECRET_KEY = 'twLBH0BoNxsC7F0dQ5Vl3W6Wzh1JSS55'
const BAIDU_OAUTH_URL = 'https://openapi.baidu.com/oauth/2.0/authorize'
const BAIDU_REDIRECT_PATH = '/settings/modules/baidu-sync'

// API 代理路径（Vite dev proxy / Nginx prod proxy）
const BAIDU_TOKEN_PATH = '/api/baidu-oauth/token'
const BAIDU_API_BASE = '/api/baidu-pan'
const BAIDU_PCS_BASE = '/api/baidu-pcs'

// 备份目录
const BACKUP_DIR = '/apps/MeowChat云端同步'

// v5 多版本备份
const MAX_BACKUP_VERSIONS = 10

function timestampToLabel(ts: number): string {
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
}

function versionedDataPath(label: string) {
  return `${BACKUP_DIR}/data-${label}.zip`
}
function versionedModelsPath(hash8: string) {
  return `${BACKUP_DIR}/models-${hash8}.zip`
}
function versionedManifestPath(label: string) {
  return `${BACKUP_DIR}/manifest-${label}.json`
}

// 备份版本信息
interface BackupVersion {
  version: number
  timestamp: number
  label: string
  dataHash: string
  modelsHash: string
  modelsFile: string
  dataFile: string // 实际 data zip 文件名（data 未变更时复用上一版本的文件）
  dataSize: number
}

// 本地快照（恢复前保存，失败时回滚）
interface LocalSnapshot {
  localStorage: Record<string, string>
  appData: Record<string, string>
  spriteData: Record<string, any>
  pluginKVData: Record<string, any>
  modelData: Record<string, any> // display-model-* keys from default localforage
}

// 百度网盘分片上传：每片 4MB（百度普通用户要求每片必须 4MB）
const CHUNK_SIZE = 4 * 1024 * 1024

// 上传重试配置（国内边缘节点直连百度，链路稳定，少量重试覆盖偶发抖动即可）
const UPLOAD_MAX_RETRIES = 4
const UPLOAD_RETRY_DELAY = 1000 // ms（首次重试 1s，递增）

// 下载重试配置
// 分片大小回到 2MB：实测百度非会员限速波动极大（19~80KB/s），4MB 分片在最坏情况下
// 单片传输超 200s 必然撞超时墙，且超时重试是整片重下、代价翻倍。小分片单片更快、
// 超时风险低、重试代价小，比"堆大超时值"更稳健（限速没有下限，加超时是军备竞赛）。
const DOWNLOAD_MAX_RETRIES = 4
const DOWNLOAD_RETRY_DELAY = 1000 // ms
const DOWNLOAD_CHUNK_SIZE = 2 * 1024 * 1024 // 2MB per chunk

async function withAbortTimeout<T>(timeoutMs: number, request: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  try {
    return await request(controller.signal)
  }
  finally {
    clearTimeout(timeoutId)
  }
}

// 这些键绑定当前浏览器/设备，不能被另一台设备的备份覆盖。
// 引导状态也保留在本机；恢复联系人后由启动检查自动跳过新手引导。
const PRESERVED_LOCAL_STORAGE_PREFIXES = [
  'baidu/',
  'license/',
  'offline-sync/',
  'onboarding/',
]
const MIGRATION_LOCAL_STORAGE_KEYS = new Set(['app/data-version', 'app/migration-history'])

function isPreservedLocalStorageKey(key: string): boolean {
  return PRESERVED_LOCAL_STORAGE_PREFIXES.some(prefix => key.startsWith(prefix))
}

// IndexedDB 实例（与项目其他模块保持一致）
const appDataStorage = localforage.createInstance({
  name: 'meowchat',
  storeName: 'app_data',
})

const spriteStorage = localforage.createInstance({
  name: 'meowchat-sprites',
  storeName: 'sprite-sets',
})

const pluginKVStorage = localforage.createInstance({
  name: 'meowchat-plugin-storage',
  storeName: 'kv',
})

type KeyValueStorage = Pick<typeof appDataStorage, 'iterate' | 'setItem' | 'removeItem'>

async function collectStorageEntries<T>(storage: KeyValueStorage, keyFilter: (key: string) => boolean = () => true): Promise<Record<string, T>> {
  const data: Record<string, T> = {}
  await storage.iterate<T, void>((value, key) => {
    if (keyFilter(key))
      data[key] = value
  })
  return data
}

/** 替换一个 localforage 存储，并在写入或清理失败时尽力恢复原值。 */
async function replaceStorageEntries<T>(
  storage: KeyValueStorage,
  incoming: Record<string, T>,
  options: { keyFilter?: (key: string) => boolean, label?: string } = {},
): Promise<void> {
  const keyFilter = options.keyFilter ?? (() => true)
  const label = options.label ?? '数据'
  const existing = await collectStorageEntries<T>(storage, keyFilter)

  try {
    for (const [key, value] of Object.entries(incoming)) {
      if (keyFilter(key))
        await storage.setItem(key, value)
    }
    for (const key of Object.keys(existing)) {
      if (!(key in incoming))
        await storage.removeItem(key)
    }
  }
  catch (error) {
    try {
      const current = await collectStorageEntries<T>(storage, keyFilter)
      for (const key of Object.keys(current)) {
        if (!(key in existing))
          await storage.removeItem(key)
      }
      for (const [key, value] of Object.entries(existing))
        await storage.setItem(key, value)
    }
    catch (rollbackError) {
      console.error(`[Baidu Sync] ${label}替换失败且回滚失败:`, rollbackError)
    }
    throw error
  }
}

// 默认 localforage 实例（存储模型文件）
// localforage 默认实例即可，与 display-models.ts 一致

// 自动同步定时器（store 外部变量，避免响应式开销）
let _autoSyncTimer: ReturnType<typeof setInterval> | null = null
let _visibilityHandler: (() => void) | null = null

export const useBaiduSyncStore = defineStore('baidu-sync', () => {
  // ============================================================
  // 持久化状态
  // ============================================================
  const accessToken = useLocalStorage('baidu/access-token', '')
  const tokenExpireTime = useLocalStorage('baidu/token-expire-time', 0)
  const requiresReauthorization = useLocalStorage('baidu/reauthorization-required', false)
  const autoSyncEnabled = useLocalStorage('baidu/auto-sync', true)
  const autoSyncPreferenceSet = useLocalStorage('baidu/auto-sync-preference-set', false)
  const syncInterval = useLocalStorage('baidu/sync-interval', 86400000)
  const syncIntervalPreferenceSet = useLocalStorage('baidu/sync-interval-preference-set', false)
  const lastSyncTime = useLocalStorage('baidu/last-sync-time', 0)
  const lastUploadedModelsHash = useLocalStorage('baidu/last-models-hash', '')
  const lastUploadedDataHash = useLocalStorage('baidu/last-data-hash', '')
  const lastUploadedDataSize = useLocalStorage('baidu/last-data-size', 0)

  // ============================================================
  // 响应式状态
  // ============================================================
  const syncStatus = ref<'disconnected' | 'connected' | 'syncing' | 'error'>('disconnected')
  const currentOperation = ref<'backup' | 'restore' | 'export' | 'import' | null>(null)
  const isBusy = computed(() => currentOperation.value !== null || syncStatus.value === 'syncing')
  const lastError = ref('')
  const baiduUserName = useLocalStorage('baidu/user-name', '')
  /** 备份/恢复进度 0~100 */
  const progressPercent = ref(0)
  /** 当前进度阶段描述 */
  const progressText = ref('')
  /** 可用备份版本列表 */
  const backupVersions = ref<BackupVersion[]>([])
  /** 正在加载版本列表 */
  const isLoadingVersions = ref(false)
  const tokenClock = ref(Date.now())
  const tokenClockTimer = typeof window !== 'undefined'
    ? window.setInterval(() => {
        tokenClock.value = Date.now()
      }, 30_000)
    : null
  const tokenVisibilityHandler = () => {
    tokenClock.value = Date.now()
  }

  if (typeof document !== 'undefined')
    document.addEventListener('visibilitychange', tokenVisibilityHandler)

  // ============================================================
  // 计算属性
  // ============================================================
  const isConfigured = computed(() => !!accessToken.value && !requiresReauthorization.value && tokenClock.value < tokenExpireTime.value)
  const isTokenExpired = computed(() => !!accessToken.value && tokenClock.value >= tokenExpireTime.value)
  const needsReauthorization = computed(() => requiresReauthorization.value || isTokenExpired.value)

  // ============================================================
  // OAuth 相关
  // ============================================================

  async function initializeConnectionStatus() {
    await handleOAuthCallback()
    if (isConfigured.value) {
      await testConnection()
    }
    else if (needsReauthorization.value) {
      syncStatus.value = 'error'
      lastError.value = formatError('百度网盘授权已失效，请重新授权', '初始化')
    }
  }

  async function handleOAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search)

    // 检查百度 OAuth 是否返回了错误（如 redirect_uri 未注册等）
    const oauthError = urlParams.get('error')
    if (oauthError) {
      const errorDesc = urlParams.get('error_description') || oauthError
      console.error('百度 OAuth 授权失败:', oauthError, errorDesc)
      syncStatus.value = 'error'
      lastError.value = `百度授权失败: ${errorDesc} (error=${oauthError})`
      window.history.replaceState(null, '', window.location.pathname)
      return
    }

    const code = urlParams.get('code')
    if (!code)
      return

    try {
      syncStatus.value = 'syncing'
      lastError.value = ''
      window.history.replaceState(null, '', window.location.pathname)
      await exchangeCodeForToken(code)
    }
    catch (error) {
      console.error('处理百度 OAuth 回调失败:', error)
      syncStatus.value = 'error'
      lastError.value = formatError(error, '授权回调')
    }
  }

  async function exchangeCodeForToken(code: string) {
    const redirectUri = window.location.origin + BAIDU_REDIRECT_PATH
    const tokenUrl = `${BAIDU_TOKEN_PATH}?grant_type=authorization_code&code=${code}&client_id=${BAIDU_APP_KEY}&client_secret=${BAIDU_SECRET_KEY}&redirect_uri=${encodeURIComponent(redirectUri)}`

    const response = await fetch(tokenUrl)
    const data = await safeParseJson(response, 'OAuth Token 交换')

    if (data.error) {
      throw new Error(`获取 Token 失败: ${data.error_description || data.error}`)
    }

    if (data.access_token) {
      if (!baiduUserName.value) {
        if (!autoSyncPreferenceSet.value)
          autoSyncEnabled.value = true
        if (!syncIntervalPreferenceSet.value)
          syncInterval.value = 86400000
      }
      accessToken.value = data.access_token
      tokenClock.value = Date.now()
      tokenExpireTime.value = Date.now() + (data.expires_in || 2592000) * 1000
      requiresReauthorization.value = false
      lastError.value = ''
    }
    else {
      throw new Error('未获取到 access_token')
    }
  }

  function startOAuth() {
    const redirectUri = window.location.origin + BAIDU_REDIRECT_PATH
    const authUrl = `${BAIDU_OAUTH_URL}?response_type=code&client_id=${BAIDU_APP_KEY}&redirect_uri=${encodeURIComponent(redirectUri)}&scope=basic,netdisk&display=popup`
    window.location.href = authUrl
  }

  async function testConnection(): Promise<boolean> {
    if (currentOperation.value)
      return isConfigured.value
    if (!accessToken.value) {
      lastError.value = formatError('未授权百度网盘，请先登录授权', '连接测试')
      return false
    }

    try {
      syncStatus.value = 'syncing'
      const response = await fetch(
        `${BAIDU_API_BASE}/xpan/nas?method=uinfo&access_token=${accessToken.value}`,
      )

      const data = await safeParseJson(response, '测试连接')

      if (data.errno && data.errno !== 0) {
        if (data.errno === -6 || data.errno === 110 || data.errno === 42001 || data.errno === 42002) {
          markAuthorizationExpired(true)
          lastError.value = formatError(`Token 已失效，请重新授权 (errno=${data.errno}: ${baiduErrnoDesc(data.errno)})`, '连接测试')
          return false
        }
        throw new Error(`百度 API 错误 errno=${data.errno}: ${baiduErrnoDesc(data.errno)}${data.errmsg ? ` (${data.errmsg})` : ''}`)
      }

      baiduUserName.value = data.baidu_name || data.netdisk_name || '百度用户'
      if (!currentOperation.value) {
        syncStatus.value = 'connected'
        lastError.value = ''
      }
      return true
    }
    catch (error) {
      if (!currentOperation.value) {
        syncStatus.value = 'error'
        lastError.value = formatError(error, '连接测试')
      }
      return false
    }
  }

  function markAuthorizationExpired(clearToken = false): void {
    requiresReauthorization.value = true
    if (clearToken) {
      accessToken.value = ''
      tokenExpireTime.value = 0
    }
    syncStatus.value = 'error'
  }

  function clearCredentials(): void {
    accessToken.value = ''
    tokenExpireTime.value = 0
    requiresReauthorization.value = false
    autoSyncEnabled.value = false
    baiduUserName.value = ''
    syncStatus.value = 'disconnected'
    lastError.value = ''
  }

  function setAutoSyncEnabled(enabled: boolean): void {
    autoSyncPreferenceSet.value = true
    autoSyncEnabled.value = enabled
  }

  function setSyncInterval(interval: number): void {
    syncIntervalPreferenceSet.value = true
    syncInterval.value = interval
  }

  // ============================================================
  // 全量数据收集
  // ============================================================

  /** 收集所有 localStorage 数据（排除百度 OAuth 凭证） */
  function collectLocalStorage(): Record<string, string> {
    const data: Record<string, string> = {}
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key)
        continue
      // 排除设备授权和同步状态；迁移版本随备份保存，恢复旧备份后可继续执行迁移。
      if (isPreservedLocalStorageKey(key))
        continue
      const value = localStorage.getItem(key)
      if (value !== null) {
        data[key] = value
      }
    }
    return data
  }

  /** 替换可同步的 localStorage 数据；老备份缺少迁移状态时保留当前版本。 */
  function replaceLocalStorage(incomingData: Record<string, string>): void {
    const incoming = { ...incomingData }
    for (const key of MIGRATION_LOCAL_STORAGE_KEYS) {
      if (!(key in incoming)) {
        const currentValue = localStorage.getItem(key)
        if (currentValue !== null)
          incoming[key] = currentValue
      }
    }

    const existing = collectLocalStorage()
    try {
      for (const [key, value] of Object.entries(incoming)) {
        if (!isPreservedLocalStorageKey(key))
          localStorage.setItem(key, value)
      }
      for (const key of Object.keys(existing)) {
        if (!(key in incoming))
          localStorage.removeItem(key)
      }
    }
    catch (error) {
      try {
        const current = collectLocalStorage()
        for (const key of Object.keys(current)) {
          if (!(key in existing))
            localStorage.removeItem(key)
        }
        for (const [key, value] of Object.entries(existing))
          localStorage.setItem(key, value)
      }
      catch (rollbackError) {
        console.error('[Baidu Sync] localStorage 替换失败且回滚失败:', rollbackError)
      }
      throw error
    }
  }

  /** 收集 IndexedDB meowchat/app_data 中的所有数据 */
  async function collectAppData(): Promise<Record<string, string>> {
    return collectStorageEntries<string>(appDataStorage)
  }

  /** 收集 meowchat-sprites/sprite-sets 中的所有数据 */
  async function collectSpriteData(): Promise<Record<string, any>> {
    return collectStorageEntries<any>(spriteStorage)
  }

  /** 收集 meowchat-plugin-storage/kv 中的所有插件数据 */
  async function collectPluginKVData(): Promise<Record<string, any>> {
    return collectStorageEntries<any>(pluginKVStorage)
  }

  /**
   * 收集默认 localforage 中的模型文件
   * 返回 { id, meta, fileArrayBuffer } 数组
   */
  async function collectModelFiles(): Promise<Array<{
    id: string
    meta: { format: string, name: string, previewImage?: string, importedAt: number }
    fileBuffer: ArrayBuffer | null
    fileName: string
  }>> {
    const models: Array<{
      id: string
      meta: { format: string, name: string, previewImage?: string, importedAt: number }
      fileBuffer: ArrayBuffer | null
      fileName: string
    }> = []

    await localforage.iterate<any, void>((val, key) => {
      if (!key.startsWith('display-model-'))
        return
      // val 是 DisplayModelFile 对象，包含 file (File), format, name, previewImage, importedAt
      if (val && val.type === 'file' && val.file instanceof File) {
        // 先记录，稍后异步读取 File 内容
        models.push({
          id: key,
          meta: {
            format: val.format,
            name: val.name || val.file.name,
            previewImage: val.previewImage,
            importedAt: val.importedAt,
          },
          fileBuffer: null, // 稍后填充
          fileName: val.file.name,
        })
      }
    })

    // 异步读取每个 File 的 ArrayBuffer
    for (const model of models) {
      try {
        const stored = await localforage.getItem<any>(model.id)
        if (stored?.file instanceof File) {
          model.fileBuffer = await stored.file.arrayBuffer()
        }
      }
      catch (err) {
        console.warn(`[Baidu Sync] 读取模型文件失败: ${model.id}`, err)
      }
    }

    return models
  }

  /**
   * 计算模型文件指纹（SHA-256 前 16 位 hex）
   * 用于判断模型是否变更，避免重复上传
   */
  async function computeModelsHash(
    models: Array<{ id: string, meta: { importedAt: number }, fileBuffer: ArrayBuffer | null }>,
  ): Promise<string> {
    if (models.length === 0)
      return 'empty'
    const parts: string[] = []
    for (const m of models) {
      let contentSample = ''
      if (m.fileBuffer && m.fileBuffer.byteLength > 0) {
        const sampleSize = Math.min(1024, m.fileBuffer.byteLength)
        const sample = new Uint8Array(m.fileBuffer, 0, sampleSize)
        contentSample = Array.from(sample).map(b => b.toString(16).padStart(2, '0')).join('')
      }
      parts.push(`${m.id}:${m.fileBuffer?.byteLength ?? 0}:${m.meta.importedAt}:${contentSample}`)
    }
    const fingerprint = parts.sort().join('|')
    const data = new TextEncoder().encode(fingerprint)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .slice(0, 16)
  }

  // ============================================================
  // 数据指纹计算
  // ============================================================

  /** 计算数据内容 hash，用于增量上传判断 */
  async function computeDataHash(
    lsData: Record<string, string>,
    appData: Record<string, string>,
    spriteData: Record<string, any>,
    pluginKVData: Record<string, any>,
  ): Promise<string> {
    const lsPart = Object.keys(lsData).sort().map(k => `${k}:${JSON.stringify(lsData[k])}`).join('|')
    const appPart = Object.keys(appData).sort().map(k => `${k}:${JSON.stringify(appData[k])}`).join('|')
    const spritePart = Object.keys(spriteData).sort().map(k => `${k}:${JSON.stringify(spriteData[k])}`).join('|')
    const pluginPart = Object.keys(pluginKVData).sort().map(k => `${k}:${JSON.stringify(pluginKVData[k])}`).join('|')
    const [lsHash, appHash, spriteHash, pluginHash] = await Promise.all([
      sha256(lsPart),
      sha256(appPart),
      sha256(spritePart),
      sha256(pluginPart),
    ])
    const combined = await sha256(`${lsHash}|${appHash}|${spriteHash}|${pluginHash}`)
    return combined.slice(0, 16)
  }

  // ============================================================
  // 打包为 ZIP
  // ============================================================

  /**
   * 打包纯数据（不含模型）为 ZIP
   * manifest version=4，不再包含 modelsHash（已独立到 manifest.json）
   */
  async function packDataZip(
    lsData: Record<string, string>,
    appData: Record<string, string>,
    spriteData: Record<string, any>,
    pluginKVData: Record<string, any>,
  ): Promise<Blob> {
    const zip = new JSZip()

    progressText.value = '打包数据...'
    progressPercent.value = 10

    zip.file('manifest.json', JSON.stringify({
      version: 4,
      timestamp: Date.now(),
      stats: {
        localStorageKeys: Object.keys(lsData).length,
        appDataKeys: Object.keys(appData).length,
        spriteKeys: Object.keys(spriteData).length,
        pluginKVKeys: Object.keys(pluginKVData).length,
      },
    }, null, 2))
    zip.file('localStorage.json', JSON.stringify(lsData))
    zip.file('appData.json', JSON.stringify(appData))
    zip.file('sprites.json', JSON.stringify(spriteData))
    zip.file('pluginKV.json', JSON.stringify(pluginKVData))

    progressText.value = '压缩数据...'
    const blob = await zip.generateAsync(
      { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } },
      (meta) => { progressPercent.value = 10 + Math.round(meta.percent * 0.05) },
    )

    console.info(`[Baidu Sync] data.zip: ${(blob.size / 1024 / 1024).toFixed(2)} MB`)
    return blob
  }

  /**
   * 打包模型文件为 ZIP
   */
  async function packModelsZip(
    modelFiles: Array<{ id: string, meta: any, fileBuffer: ArrayBuffer | null, fileName: string }>,
  ): Promise<Blob> {
    const zip = new JSZip()

    zip.file('manifest.json', JSON.stringify({
      version: 3,
      type: 'models',
      timestamp: Date.now(),
      modelCount: modelFiles.length,
    }))

    const modelsFolder = zip.folder('models')
    if (!modelsFolder)
      throw new Error('无法创建 models 文件夹')
    for (let i = 0; i < modelFiles.length; i++) {
      const model = modelFiles[i]
      progressText.value = `打包模型 (${i + 1}/${modelFiles.length}): ${model.meta.name}`

      modelsFolder.file(`${model.id}.meta.json`, JSON.stringify(model.meta))
      if (model.fileBuffer) {
        modelsFolder.file(`${model.id}.bin`, model.fileBuffer)
      }

      progressPercent.value = 50 + Math.round((i + 1) / modelFiles.length * 10)
    }

    progressText.value = '压缩模型...'
    const blob = await zip.generateAsync(
      { type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } },
      (meta) => { progressPercent.value = 60 + Math.round(meta.percent * 0.05) },
    )

    console.info(`[Baidu Sync] models.zip: ${(blob.size / 1024 / 1024).toFixed(2)} MB`)
    return blob
  }

  // ============================================================
  // 从 ZIP 恢复数据
  // ============================================================

  /**
   * 从 ZIP 中恢复纯数据部分（localStorage + appData + sprites + pluginKV）
   * @returns manifest 中的 modelsHash（v3 兼容）
   */
  async function unpackDataZip(zip: JSZip): Promise<{ modelsHash: string }> {
    const manifestFile = zip.file('manifest.json')
    if (!manifestFile)
      throw new Error('备份文件格式错误：缺少 manifest.json')

    const manifest = JSON.parse(await manifestFile.async('text'))
    console.info('[Baidu Sync] 恢复数据备份:', manifest)

    const localStorageFile = zip.file('localStorage.json')
    const localStorageData = localStorageFile
      ? JSON.parse(await localStorageFile.async('text')) as Record<string, string>
      : null
    const appDataFile = zip.file('appData.json')
    const appData = appDataFile
      ? JSON.parse(await appDataFile.async('text')) as Record<string, string>
      : null
    const spritesFile = zip.file('sprites.json')
    const spriteData = spritesFile
      ? JSON.parse(await spritesFile.async('text')) as Record<string, any>
      : null
    const pluginKVFile = zip.file('pluginKV.json')
    const pluginKVData = pluginKVFile
      ? JSON.parse(await pluginKVFile.async('text')) as Record<string, any>
      : null

    const stats = manifest.stats as Record<string, unknown> | undefined
    const sections = [
      { name: 'localStorage', expected: stats?.localStorageKeys, data: localStorageData },
      { name: '应用数据', expected: stats?.appDataKeys, data: appData },
      { name: '立绘数据', expected: stats?.spriteKeys, data: spriteData },
      { name: '插件数据', expected: stats?.pluginKVKeys, data: pluginKVData },
    ]
    for (const section of sections) {
      if (typeof section.expected === 'number'
        && ((section.expected > 0 && !section.data)
          || (section.data && Object.keys(section.data).length !== section.expected))) {
        throw new Error(`备份中的${section.name}不完整，已取消恢复以保护本地数据`)
      }
    }

    // 1. 恢复 localStorage
    progressText.value = '恢复 localStorage...'
    if (localStorageData)
      replaceLocalStorage(localStorageData)

    // 2. 恢复 IndexedDB app_data
    progressText.value = '恢复应用数据...'
    if (appData)
      await replaceStorageEntries(appDataStorage, appData, { label: '应用数据' })

    // 3. 恢复 Sprite 数据
    progressText.value = '恢复立绘数据...'
    if (spriteData)
      await replaceStorageEntries(spriteStorage, spriteData, { label: '立绘数据' })

    // 4. 恢复插件 KV 数据（v4 新增，旧备份无此文件则跳过）
    if (pluginKVData) {
      progressText.value = '恢复插件数据...'
      await replaceStorageEntries(pluginKVStorage, pluginKVData, { label: '插件数据' })
    }

    return {
      modelsHash: manifest.modelsHash || '',
    }
  }

  /**
   * 从 ZIP 中恢复模型文件
   */
  async function unpackModelsZip(zip: JSZip): Promise<void> {
    const modelsFolder = zip.folder('models')
    if (!modelsFolder)
      return

    const manifestFile = zip.file('manifest.json')
    const manifest = manifestFile ? JSON.parse(await manifestFile.async('text')) as { modelCount?: number } : null

    // 收集所有 meta 文件
    const metaFiles: string[] = []
    modelsFolder.forEach((relativePath) => {
      if (relativePath.endsWith('.meta.json')) {
        metaFiles.push(relativePath)
      }
    })
    if (typeof manifest?.modelCount === 'number' && metaFiles.length !== manifest.modelCount)
      throw new Error('备份中的模型清单不完整，已取消恢复')

    const modelData: Record<string, any> = {}
    for (let i = 0; i < metaFiles.length; i++) {
      const metaPath = metaFiles[i]
      const modelId = metaPath.replace('.meta.json', '')
      const binPath = `${modelId}.bin`

      progressText.value = `恢复模型 (${i + 1}/${metaFiles.length})...`

      const metaFile = modelsFolder.file(metaPath)
      if (!metaFile)
        throw new Error(`备份中的模型元数据缺失: ${metaPath}`)

      const meta = JSON.parse(await metaFile.async('text')) as {
        format?: unknown
        name?: unknown
        previewImage?: unknown
        importedAt?: unknown
      }
      if (!meta || typeof meta !== 'object' || typeof meta.format !== 'string' || typeof meta.name !== 'string')
        throw new Error(`备份中的模型元数据无效: ${metaPath}`)

      const binFile = modelsFolder.file(binPath)
      const file = binFile
        ? new File([await binFile.async('arraybuffer')], meta.name, { type: 'application/octet-stream' })
        : new File(
          [JSON.stringify({ type: 'sprite-set' })],
          `${meta.name}.sprite`,
          { type: 'application/json' },
        )

      modelData[modelId] = {
        id: modelId,
        format: meta.format,
        type: 'file',
        file,
        name: meta.name,
        previewImage: typeof meta.previewImage === 'string' ? meta.previewImage : undefined,
        importedAt: typeof meta.importedAt === 'number' ? meta.importedAt : Date.now(),
      }
    }

    await replaceStorageEntries(localforage, modelData, {
      keyFilter: key => key.startsWith('display-model-'),
      label: '模型数据',
    })
  }

  /**
   * 从全量备份 ZIP 恢复（向后兼容旧格式 meowchat-backup.zip）
   * 内部调用 unpackDataZip + unpackModelsZip
   */
  async function unpackFromZip(zipBlob: Blob): Promise<void> {
    const zip = await JSZip.loadAsync(zipBlob)

    await unpackDataZip(zip)
    progressPercent.value = 50

    await unpackModelsZip(zip)
    progressPercent.value = 95
  }

  async function exportToFile(): Promise<void> {
    if (currentOperation.value)
      return
    currentOperation.value = 'export'
    try {
      syncStatus.value = 'syncing'
      progressPercent.value = 0
      progressText.value = '准备导出...'
      lastError.value = ''

      const modelFiles = await collectModelFiles()
      const lsData = collectLocalStorage()
      const appData = await collectAppData()
      const spriteData = await collectSpriteData()
      const pluginKVData = await collectPluginKVData()
      progressPercent.value = 30

      const label = timestampToLabel(Date.now())
      progressText.value = '打包数据...'
      const dataZip = await packDataZip(lsData, appData, spriteData, pluginKVData)
      progressPercent.value = 60

      const modelsZip = await packModelsZip(modelFiles)
      progressPercent.value = 90

      // 合并两个 zip（models 先写，data 后写，确保 data/manifest.json 优先）
      const finalZip = new JSZip()
      const dataZipContent = await JSZip.loadAsync(dataZip)
      const modelsZipContent = await JSZip.loadAsync(modelsZip)
      for (const [path, file] of Object.entries(modelsZipContent.files)) {
        if (!file.dir)
          finalZip.file(path, await file.async('arraybuffer'))
      }
      for (const [path, file] of Object.entries(dataZipContent.files)) {
        if (!file.dir)
          finalZip.file(path, await file.async('arraybuffer'))
      }

      progressText.value = '生成文件...'
      const blob = await finalZip.generateAsync({ type: 'blob', compression: 'DEFLATE' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `meowchat-backup-${label}.zip`
      // Safari 需要已挂载的链接，并异步读取 Blob URL，不能点击后立即释放。
      document.body.appendChild(a)
      try {
        a.click()
      }
      finally {
        a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 60_000)
      }

      syncStatus.value = isConfigured.value ? 'connected' : 'disconnected'
      progressPercent.value = 100
      progressText.value = '导出完成'
      showSuccess('备份文件已生成，请在浏览器下载列表中查看', 5000)
    }
    catch (error) {
      syncStatus.value = 'error'
      lastError.value = formatError(error, '本地导出')
      progressText.value = '导出失败'
      showError(lastError.value, 6000)
    }
    finally {
      currentOperation.value = null
    }
  }

  async function importFromFile(file: File): Promise<void> {
    if (currentOperation.value)
      return
    currentOperation.value = 'import'
    try {
      syncStatus.value = 'syncing'
      progressPercent.value = 0
      progressText.value = '读取文件...'
      lastError.value = ''

      const blob = new Blob([await file.arrayBuffer()])
      progressPercent.value = 10

      progressText.value = '创建本地快照...'
      const snapshot = await createLocalSnapshot()
      try {
        await unpackFromZip(blob)
      }
      catch (restoreError) {
        progressText.value = '导入失败，正在回滚...'
        try {
          await restoreLocalSnapshot(snapshot)
        }
        catch (rollbackError) {
          console.error('[Baidu Sync] 本地导入回滚失败:', rollbackError)
        }
        throw restoreError
      }

      syncStatus.value = isConfigured.value ? 'connected' : 'disconnected'
      progressPercent.value = 100
      progressText.value = '导入完成，即将刷新...'
      setTimeout(() => window.location.reload(), 1500)
    }
    catch (error) {
      syncStatus.value = 'error'
      lastError.value = formatError(error, '本地导入')
      progressText.value = '导入失败'
    }
    finally {
      currentOperation.value = null
    }
  }

  // ============================================================
  // 错误处理工具
  // ============================================================

  function baiduErrnoDesc(errno: number): string {
    const map: Record<number, string> = {
      0: '成功',
      [-1]: '服务器内部错误',
      [-2]: '参数错误',
      [-3]: '文件不存在',
      [-4]: '账号未激活或被封禁',
      [-5]: '没有权限',
      [-6]: 'Token 无效或已过期',
      [-7]: '文件或目录名非法',
      [-8]: '文件或目录已存在',
      [-9]: '文件或目录不存在',
      [-10]: '云端容量已满',
      2: '参数错误',
      6: '不允许接入用户数据',
      10: '创建文件失败',
      110: 'Token 已过期',
      111: '有其他异步任务正在执行',
      31023: '用户未授权该应用',
      31024: '应用未上线或已下线',
      31034: '命中接口频控',
      42000: '没有权限',
      42001: 'Token 无效',
      42002: 'Token 已过期',
      42999: '应用被禁用',
      20011: '应用审核中，仅限10个测试用户',
      20012: '访问超限，调用次数已达上限',
      20013: '权限不足，当前应用无接口权限',
    }
    return map[errno] ?? '未知错误'
  }

  function formatError(error: unknown, step?: string): string {
    const ts = new Date().toLocaleTimeString('zh-CN', { hour12: false })
    const prefix = step ? `[${step}]` : ''
    const msg = String(error instanceof Error ? error.message : error)

    if (msg.includes('ECONNRESET') || msg.includes('ECONNREFUSED'))
      return `${prefix} 网络连接被重置，请重试 (${ts})`
    if (msg.includes('fetch') || msg.includes('Failed to fetch') || msg.includes('NetworkError'))
      return `${prefix} 网络请求失败，请检查网络 (${ts})`
    if (msg.includes('AbortError') || msg.includes('abort'))
      return `${prefix} 请求超时，请重试 (${ts})`

    // 已经是格式化过的错误消息，只加时间戳
    return `${prefix} ${msg} (${ts})`
  }

  // ============================================================
  // 百度网盘分片上传
  // ============================================================

  /**
   * 安全解析 JSON 响应
   * 防止服务器返回 HTML 错误页时 .json() 崩溃
   */
  async function safeParseJson(response: Response, context: string): Promise<any> {
    const text = await response.text()
    try {
      const data = JSON.parse(text)
      if ([-6, 110, 42001, 42002].includes(data?.errno))
        markAuthorizationExpired()
      return data
    }
    catch {
      // 服务器返回了非 JSON（可能是 HTML 错误页、WAF 拦截页、504 页面等）
      const preview = text.slice(0, 200)
      throw new Error(`${context}: 服务器返回非 JSON 响应 (HTTP ${response.status})。响应内容: ${preview}`)
    }
  }

  /**
   * 带重试的分片上传
   * 跨国网络不稳定，需要自动重试
   */
  async function uploadChunkWithRetry(
    url: string,
    formData: FormData,
    chunkIndex: number,
    chunkCount: number,
  ): Promise<any> {
    for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        progressText.value = `上传分片 (${chunkIndex + 1}/${chunkCount})${attempt > 1 ? ` 重试 ${attempt}/${UPLOAD_MAX_RETRIES}` : ''}...`

        // 单个分片超时 120 秒（4MB 分片，即使网络慢也应该够了）
        const uploadData = await withAbortTimeout(120_000, async (signal) => {
          const uploadRes = await fetch(url, {
            method: 'POST',
            body: formData,
            signal,
          })
          return safeParseJson(uploadRes, `分片 ${chunkIndex} 上传`)
        })

        if (uploadData.error_code) {
          throw new Error(`分片 ${chunkIndex} 上传失败: ${uploadData.error_msg || uploadData.error_code}`)
        }

        return uploadData
      }
      catch (error: any) {
        const isLastAttempt = attempt === UPLOAD_MAX_RETRIES
        const isAbort = error?.name === 'AbortError'
        const errorMsg = isAbort ? '上传超时' : String(error?.message || error)

        console.warn(`[Baidu Sync] 分片 ${chunkIndex} 第 ${attempt} 次尝试失败: ${errorMsg}`)

        if (isLastAttempt) {
          throw new Error(`分片 ${chunkIndex} 上传失败（已重试 ${UPLOAD_MAX_RETRIES} 次）: ${errorMsg}`)
        }

        // 等待后重试，递增但最多 3s
        const delay = Math.min(UPLOAD_RETRY_DELAY * attempt, 3000)
        progressText.value = `分片 ${chunkIndex + 1} 上传失败，${Math.round(delay / 1000)}s 后重试 (${attempt}/${UPLOAD_MAX_RETRIES})...`
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    // 兜底：循环内要么 return 分片，要么在最后一次尝试(isLastAttempt)抛错，
    // 正常控制流不会到这里。仅为满足 TS 返回值类型检查而保留。
    throw new Error('分片上传异常退出重试循环')
  }

  /**
   * 将 Blob 分片上传到百度网盘指定路径
   * 流程: precreate → upload chunks (带重试) → create
   */
  async function uploadFileToBaidu(
    blob: Blob,
    filePath: string,
    progressStart = 60,
    progressEnd = 95,
  ): Promise<void> {
    const totalSize = blob.size
    const chunkCount = Math.ceil(totalSize / CHUNK_SIZE)

    console.info(`[Baidu Sync] 开始上传 ${filePath}: ${(totalSize / 1024 / 1024).toFixed(2)} MB, ${chunkCount} 个分片`)

    const dummyBlockList = Array.from({ length: chunkCount }, (_, i) =>
      `0000000000000000000000000000000${i}`.slice(-32))

    // 1. precreate（带重试）
    progressText.value = '预创建文件...'
    const precreateBody = new URLSearchParams({
      path: filePath,
      size: totalSize.toString(),
      isdir: '0',
      autoinit: '1',
      rtype: '3',
      block_list: JSON.stringify(dummyBlockList),
    })

    let precreateData: any
    for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        const precreateRes = await fetch(`${BAIDU_API_BASE}/xpan/file?method=precreate&access_token=${accessToken.value}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: precreateBody.toString(),
        })
        precreateData = await safeParseJson(precreateRes, '预创建文件')
        if (precreateData.errno === 0)
          break
        throw new Error(`预上传失败 errno=${precreateData.errno}: ${baiduErrnoDesc(precreateData.errno)}`)
      }
      catch (error: any) {
        if (attempt === UPLOAD_MAX_RETRIES)
          throw error
        console.warn(`[Baidu Sync] precreate 第 ${attempt} 次失败: ${error?.message}`)
        const delay = Math.min(UPLOAD_RETRY_DELAY * attempt, 3000)
        progressText.value = `预创建失败，${Math.round(delay / 1000)}s 后重试 (${attempt}/${UPLOAD_MAX_RETRIES})...`
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }

    const uploadId = precreateData.uploadid

    // 2. 分片上传（带重试）
    const uploadedMD5s: string[] = []

    for (let i = 0; i < chunkCount; i++) {
      const start = i * CHUNK_SIZE
      const end = Math.min(start + CHUNK_SIZE, totalSize)
      const chunk = blob.slice(start, end)

      progressPercent.value = progressStart + Math.round((i + 1) / chunkCount * (progressEnd - progressStart))

      const uploadParams = new URLSearchParams({
        method: 'upload',
        access_token: accessToken.value,
        type: 'tmpfile',
        path: filePath,
        uploadid: uploadId,
        partseq: i.toString(),
      })

      const formData = new FormData()
      formData.append('file', chunk, 'chunk')

      const uploadData = await uploadChunkWithRetry(
        `${BAIDU_PCS_BASE}/pcs/superfile2?${uploadParams}`,
        formData,
        i,
        chunkCount,
      )

      uploadedMD5s.push(uploadData.md5)
    }

    // 3. create（带重试）
    progressText.value = '创建文件...'
    const createBody = new URLSearchParams({
      path: filePath,
      size: totalSize.toString(),
      isdir: '0',
      rtype: '3',
      uploadid: uploadId,
      block_list: JSON.stringify(uploadedMD5s),
    })

    for (let attempt = 1; attempt <= UPLOAD_MAX_RETRIES; attempt++) {
      try {
        const createRes = await fetch(`${BAIDU_API_BASE}/xpan/file?method=create&access_token=${accessToken.value}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: createBody.toString(),
        })
        const createData = await safeParseJson(createRes, '创建文件')
        if (createData.errno === 0)
          break
        throw new Error(`创建文件失败 errno=${createData.errno}: ${baiduErrnoDesc(createData.errno)}`)
      }
      catch (error: any) {
        if (attempt === UPLOAD_MAX_RETRIES)
          throw error
        console.warn(`[Baidu Sync] create 第 ${attempt} 次失败: ${error?.message}`)
        const delay = Math.min(UPLOAD_RETRY_DELAY * attempt, 3000)
        progressText.value = `创建文件失败，${Math.round(delay / 1000)}s 后重试 (${attempt}/${UPLOAD_MAX_RETRIES})...`
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  // ============================================================
  // 百度网盘下载
  // ============================================================

  /**
   * 下载单个分片（带重试）
   * 使用 Range 头实现断点续传，每片独立重试
   */
  async function downloadChunkWithRetry(
    downloadPath: string,
    start: number,
    end: number,
    chunkIndex: number,
    totalChunks: number,
  ): Promise<ArrayBuffer> {
    for (let attempt = 1; attempt <= DOWNLOAD_MAX_RETRIES; attempt++) {
      try {
        progressText.value = `下载中 (${chunkIndex + 1}/${totalChunks})${attempt > 1 ? ` 重试 ${attempt}/${DOWNLOAD_MAX_RETRIES}` : ''}...`

        // 单个分片超时 180 秒：2MB 分片在百度非会员最坏限速下纯传输需 100~175s
        // （实测限速波动剧烈，可低至 ~12KB/s），加 TLS 握手 + dlink 302 跟随，180s 留足裕度。
        const { res, buffer } = await withAbortTimeout(180_000, async (signal) => {
          const res = await fetch(downloadPath, {
            signal,
            headers: { Range: `bytes=${start}-${end - 1}` },
          })
          if (!res.ok)
            throw new Error(`HTTP ${res.status}`)
          return { res, buffer: await res.arrayBuffer() }
        })

        const expectedSize = end - start

        // 服务器忽略 Range 头返回了完整文件，截取需要的部分
        if (res.status === 200 && buffer.byteLength > expectedSize) {
          console.warn(`[Baidu Sync] 分片 ${chunkIndex}: 服务器返回 200 而非 206，Range 头可能未生效`)
          return buffer.slice(start, end)
        }

        return buffer
      }
      catch (error: any) {
        const isLastAttempt = attempt === DOWNLOAD_MAX_RETRIES
        const isAbort = error?.name === 'AbortError'
        const errorMsg = isAbort ? '下载超时' : String(error?.message || error)

        console.warn(`[Baidu Sync] 分片 ${chunkIndex} 第 ${attempt} 次尝试失败: ${errorMsg}`)

        if (isLastAttempt) {
          throw new Error(`分片 ${chunkIndex} 下载失败（已重试 ${DOWNLOAD_MAX_RETRIES} 次）: ${errorMsg}`)
        }

        const delay = Math.min(DOWNLOAD_RETRY_DELAY * attempt, 3000)
        progressText.value = `分片 ${chunkIndex + 1} 下载失败，${Math.round(delay / 1000)}s 后重试 (${attempt}/${DOWNLOAD_MAX_RETRIES})...`
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    // 兜底：循环内要么 return 分片，要么在最后一次尝试(isLastAttempt)抛错，
    // 正常控制流不会到这里。仅为满足 TS 返回值类型检查而保留。
    throw new Error('分片下载异常退出重试循环')
  }

  /**
   * 列出百度网盘备份目录中的文件
   */
  async function listBaiduDirectory(): Promise<Array<{ fs_id: number, server_filename: string, size: number }>> {
    progressText.value = '查找备份文件...'
    const listParams = new URLSearchParams({
      method: 'list',
      access_token: accessToken.value,
      dir: '/apps/MeowChat云端同步',
      order: 'time',
      desc: '1',
      limit: '20',
    })

    let lastErr: unknown = new Error('获取文件列表失败（未知原因）')
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const listRes = await fetch(`${BAIDU_API_BASE}/xpan/file?${listParams}`)
        const listData = await safeParseJson(listRes, '获取文件列表')
        if (listData.errno !== 0) {
          if (listData.errno === -9)
            return [] // 目录不存在，新用户从未备份过
          throw new Error(`获取文件列表失败 errno=${listData.errno}: ${baiduErrnoDesc(listData.errno)}`)
        }
        return listData.list || []
      }
      catch (e) {
        lastErr = e
        // token 类错误不重试，直接抛出
        if (e instanceof Error && /errno=(?:-6|110|42001|42002)/.test(e.message))
          throw e
        if (attempt < 3)
          await new Promise(r => setTimeout(r, 2000))
      }
    }
    throw lastErr
  }

  /**
   * 从百度网盘下载单个文件
   * @param fsId 文件的 fs_id
   * @param fileSize 文件大小（字节）
   * @param progressStart 进度起始百分比
   * @param progressEnd 进度结束百分比
   */
  async function downloadFileFromBaidu(
    fsId: number,
    fileSize: number,
    progressStart = 15,
    progressEnd = 25,
  ): Promise<Blob> {
    // 获取下载链接（dlink）
    progressText.value = '获取下载链接...'
    const metaParams = new URLSearchParams({
      method: 'filemetas',
      access_token: accessToken.value,
      fsids: JSON.stringify([fsId]),
      dlink: '1',
    })

    const metaRes = await fetch(`${BAIDU_API_BASE}/xpan/multimedia?${metaParams}`)
    const metaData = await safeParseJson(metaRes, '获取文件元信息')

    if (metaData.errno !== 0) {
      throw new Error(`获取文件信息失败 errno=${metaData.errno}: ${baiduErrnoDesc(metaData.errno)}`)
    }

    const dlink = metaData.list?.[0]?.dlink
    if (!dlink) {
      throw new Error('无法获取文件下载链接')
    }
    // 入参 fileSize 缺失时回退到 filemetas 返回的 size（不直接改写入参）
    let effectiveSize = fileSize
    if (!effectiveSize || effectiveSize <= 0) {
      effectiveSize = metaData.list?.[0]?.size
      if (!effectiveSize || effectiveSize <= 0)
        throw new Error('无法获取文件大小')
    }

    // 分片下载文件（通过代理避免 CORS，支持断点续传）
    progressText.value = '下载文件...'
    progressPercent.value = progressStart

    const dlinkUrl = new URL(dlink)
    const downloadPath = `/api/baidu-dl${dlinkUrl.pathname}?${dlinkUrl.searchParams.toString()}&access_token=${accessToken.value}`

    const totalChunks = Math.ceil(effectiveSize / DOWNLOAD_CHUNK_SIZE)
    const chunks: ArrayBuffer[] = []

    console.info(`[Baidu Sync] 开始分片下载: ${(effectiveSize / 1024 / 1024).toFixed(2)} MB, ${totalChunks} 个分片`)

    for (let i = 0; i < totalChunks; i++) {
      const start = i * DOWNLOAD_CHUNK_SIZE
      const end = Math.min(start + DOWNLOAD_CHUNK_SIZE, effectiveSize)

      const chunk = await downloadChunkWithRetry(downloadPath, start, end, i, totalChunks)
      chunks.push(chunk)

      progressPercent.value = progressStart + Math.round((i + 1) / totalChunks * (progressEnd - progressStart))
    }

    console.info(`[Baidu Sync] 下载完成，合并 ${totalChunks} 个分片`)
    return new Blob(chunks)
  }

  // ============================================================
  // 独立 manifest 上传/下载
  // ============================================================

  /** 从百度网盘下载 manifest.json，不存在则返回 null */
  async function downloadManifest(
    fileList: Array<{ fs_id: number, server_filename: string, size: number }>,
  ): Promise<{ version: number, dataHash: string, modelsHash: string } | null> {
    const manifestFile = fileList.find(f => f.server_filename === 'meowchat-manifest.json')
    if (!manifestFile)
      return null
    try {
      const blob = await downloadFileFromBaidu(manifestFile.fs_id, manifestFile.size, 5, 8)
      const text = await blob.text()
      return JSON.parse(text)
    }
    catch (err) {
      console.warn('[Baidu Sync] 下载 manifest.json 失败，回退到旧格式:', err)
      return null
    }
  }

  // ============================================================
  // v5 多版本备份辅助函数
  // ============================================================

  /** 下载小型 JSON 文件（manifest 专用，不更新进度条） */
  async function downloadJsonFile(fsId: number): Promise<Partial<BackupVersion> & { version: number }> {
    const metaParams = new URLSearchParams({
      method: 'filemetas',
      access_token: accessToken.value,
      fsids: JSON.stringify([fsId]),
      dlink: '1',
    })
    const metaRes = await fetch(`${BAIDU_API_BASE}/xpan/multimedia?${metaParams}`)
    const metaData = await safeParseJson(metaRes, '获取文件元信息')
    if (metaData.errno !== 0)
      throw new Error(`获取文件信息失败 errno=${metaData.errno}: ${baiduErrnoDesc(metaData.errno)}`)
    const dlink = metaData.list?.[0]?.dlink
    if (!dlink)
      throw new Error('无法获取文件下载链接')
    const dlinkUrl = new URL(dlink)
    const downloadPath = `/api/baidu-dl${dlinkUrl.pathname}?${dlinkUrl.searchParams.toString()}&access_token=${accessToken.value}`
    const res = await fetch(downloadPath)
    if (!res.ok)
      throw new Error(`HTTP ${res.status}`)
    const text = await res.text()
    try {
      return JSON.parse(text)
    }
    catch {
      throw new Error(`文件内容解析失败，服务器返回: ${text.slice(0, 200)}`)
    }
  }

  /** 创建本地数据快照（恢复前调用，失败时回滚） */
  async function createLocalSnapshot(): Promise<LocalSnapshot> {
    return {
      localStorage: collectLocalStorage(),
      appData: await collectAppData(),
      spriteData: await collectSpriteData(),
      pluginKVData: await collectPluginKVData(),
      modelData: await collectStorageEntries<any>(localforage, key => key.startsWith('display-model-')),
    }
  }

  /** 从快照恢复本地数据 */
  async function restoreLocalSnapshot(snapshot: LocalSnapshot): Promise<void> {
    replaceLocalStorage(snapshot.localStorage)
    await replaceStorageEntries(appDataStorage, snapshot.appData, { label: '应用数据回滚' })
    await replaceStorageEntries(spriteStorage, snapshot.spriteData, { label: '立绘数据回滚' })
    await replaceStorageEntries(pluginKVStorage, snapshot.pluginKVData, { label: '插件数据回滚' })
    await replaceStorageEntries(localforage, snapshot.modelData, {
      keyFilter: key => key.startsWith('display-model-'),
      label: '模型数据回滚',
    })
  }

  /** 获取所有可用备份版本（按时间降序） */
  async function listBackupVersions(): Promise<BackupVersion[]> {
    isLoadingVersions.value = true
    try {
      const fileList = await listBaiduDirectory()
      const manifestFiles = fileList.filter(
        f => f.server_filename.startsWith('manifest-') && f.server_filename.endsWith('.json'),
      )
      if (manifestFiles.length === 0)
        return []

      const results = await Promise.allSettled(
        manifestFiles.map(f => downloadJsonFile(f.fs_id)),
      )
      const versions = results
        .filter((r): r is PromiseFulfilledResult<BackupVersion> => r.status === 'fulfilled' && r.value?.version === 5)
        .map(r => r.value)
        .sort((a, b) => b.timestamp - a.timestamp)

      backupVersions.value = versions
      return versions
    }
    finally {
      isLoadingVersions.value = false
    }
  }

  /** 删除旧格式备份文件（meowchat-data.zip 等） */
  async function deleteOldFormatFiles(
    fileList: Array<{ server_filename: string }>,
  ): Promise<void> {
    const oldNames = ['meowchat-backup.zip', 'meowchat-data.zip', 'meowchat-models.zip', 'meowchat-manifest.json']
    const toDelete = oldNames
      .filter(name => fileList.some(f => f.server_filename === name))
      .map(name => `${BACKUP_DIR}/${name}`)
    if (toDelete.length === 0)
      return
    try {
      const deleteParams = new URLSearchParams({ method: 'filemanager', access_token: accessToken.value, opera: 'delete' })
      await fetch(`${BAIDU_API_BASE}/xpan/file?${deleteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ async: '0', filelist: JSON.stringify(toDelete) }),
      })
      console.info('[Baidu Sync] 已清理旧格式备份文件')
    }
    catch (err) {
      console.warn('[Baidu Sync] 清理旧格式文件失败:', err)
    }
  }

  /** 清理超出保留数量的旧版本备份 */
  async function cleanupOldBackups(
    fileList: Array<{ fs_id: number, server_filename: string, size: number }>,
  ): Promise<void> {
    const manifestFiles = fileList
      .filter(f => f.server_filename.startsWith('manifest-') && f.server_filename.endsWith('.json'))
      .sort((a, b) => b.server_filename.localeCompare(a.server_filename))

    // 清理旧格式文件（只要有至少一个新格式备份就清理）
    if (manifestFiles.length > 0)
      await deleteOldFormatFiles(fileList)

    if (manifestFiles.length <= MAX_BACKUP_VERSIONS)
      return

    const toKeep = manifestFiles.slice(0, MAX_BACKUP_VERSIONS)
    const toDelete = manifestFiles.slice(MAX_BACKUP_VERSIONS)

    // 收集保留版本引用的模型文件；任一 manifest 读取失败则放弃清理，避免误删
    const inUseModels = new Set<string>()
    const manifestResults = await Promise.allSettled(
      toKeep.map(async (mf) => {
        const manifest = await downloadJsonFile(mf.fs_id)
        if (manifest.modelsFile)
          inUseModels.add(manifest.modelsFile)
      }),
    )
    if (manifestResults.some(r => r.status === 'rejected')) {
      console.warn('[Baidu Sync] 部分 manifest 读取失败，跳过本次清理以避免误删模型文件')
      return
    }

    const filesToDelete: string[] = []
    for (const mf of toDelete) {
      filesToDelete.push(`${BACKUP_DIR}/${mf.server_filename}`)
      const dataName = mf.server_filename.replace('manifest-', 'data-').replace('.json', '.zip')
      if (fileList.some(f => f.server_filename === dataName))
        filesToDelete.push(`${BACKUP_DIR}/${dataName}`)
    }

    // 删除无引用的模型文件
    for (const f of fileList) {
      if (f.server_filename.startsWith('models-') && f.server_filename.endsWith('.zip') && !inUseModels.has(f.server_filename))
        filesToDelete.push(`${BACKUP_DIR}/${f.server_filename}`)
    }

    if (filesToDelete.length === 0)
      return

    try {
      const deleteParams = new URLSearchParams({ method: 'filemanager', access_token: accessToken.value, opera: 'delete' })
      await fetch(`${BAIDU_API_BASE}/xpan/file?${deleteParams}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ async: '0', filelist: JSON.stringify(filesToDelete) }),
      })
      console.info(`[Baidu Sync] 清理旧备份: 删除 ${filesToDelete.length} 个文件`)
    }
    catch (err) {
      console.warn('[Baidu Sync] 清理旧备份失败:', err)
    }
  }

  async function restoreFromLegacyJson(blob: Blob): Promise<void> {
    const text = await blob.text()
    const data = JSON.parse(text)

    if (data.data?.localStorage) {
      for (const [key, value] of Object.entries(data.data.localStorage)) {
        if (isPreservedLocalStorageKey(key))
          continue
        if (typeof value === 'string') {
          localStorage.setItem(key, value)
        }
      }
    }
  }

  // ============================================================
  // 公开方法：备份 & 恢复
  // ============================================================

  /**
   * 备份到百度网盘（v5 多版本：时间戳命名 + 模型去重 + 自动清理）
   */
  async function backupNow(automatic = false): Promise<boolean> {
    if (currentOperation.value)
      return false
    if (!isConfigured.value) {
      const msg = needsReauthorization.value ? '百度网盘授权已失效，请重新授权' : '未授权百度网盘，请先登录授权'
      lastError.value = formatError(msg, '备份')
      syncStatus.value = 'error'
      if (!automatic)
        showError(msg, 6000)
      return false
    }

    currentOperation.value = 'backup'
    try {
      syncStatus.value = 'syncing'
      progressPercent.value = 0
      progressText.value = '开始备份...'
      lastError.value = ''

      const label = timestampToLabel(Date.now())

      // 1. 收集数据并计算 hash（0~10%）
      progressText.value = '收集数据...'
      const modelFiles = await collectModelFiles()
      const modelsHash = await computeModelsHash(modelFiles)
      const lsData = collectLocalStorage()
      const appData = await collectAppData()
      const spriteData = await collectSpriteData()
      const pluginKVData = await collectPluginKVData()
      const dataHash = await computeDataHash(lsData, appData, spriteData, pluginKVData)
      progressPercent.value = 10

      const dataChanged = dataHash !== lastUploadedDataHash.value
      const modelsChanged = modelsHash !== lastUploadedModelsHash.value

      if (!dataChanged && !modelsChanged) {
        lastSyncTime.value = Date.now()
        syncStatus.value = 'connected'
        progressPercent.value = 100
        progressText.value = '数据无变化，跳过上传'
        if (!automatic)
          showSuccess('数据没有变化，已有备份仍然有效')
        console.info('[Baidu Sync] 数据和模型均无变化，跳过备份')
        return true
      }

      // 2. 检查云端已有文件（用于模型去重 + data 文件复用）
      progressText.value = '检查云端文件...'
      const hash8 = modelsHash.slice(0, 8)
      const modelsFileName = `models-${hash8}.zip`
      const fileListBefore = await listBaiduDirectory()
      const modelsAlreadyUploaded = fileListBefore.some(f => f.server_filename === modelsFileName)
      let expectedDataSize = lastUploadedDataSize.value
      let latestManifest: Partial<BackupVersion> | null = null
      if (automatic || !dataChanged) {
        const latestManifestFile = fileListBefore
          .filter(f => f.server_filename.startsWith('manifest-') && f.server_filename.endsWith('.json'))
          .sort((a, b) => b.server_filename.localeCompare(a.server_filename))[0]
        if (latestManifestFile) {
          try {
            latestManifest = await downloadJsonFile(latestManifestFile.fs_id)
            if (automatic)
              expectedDataSize = Math.max(expectedDataSize, latestManifest.dataSize || 0)
          }
          catch (error) {
            if (automatic)
              throw new Error(`无法确认最新备份大小，已暂停自动覆盖以保护云端数据: ${String(error)}`)
            console.warn('[Baidu Sync] 读取最新 manifest 失败，将上传新的 data 文件:', error)
          }
        }
      }

      // data 未变更时，从最新 manifest 继承旧的 dataFile 名（避免 manifest 指向不存在的文件）
      let dataFileName = `data-${label}.zip`
      if (!dataChanged && latestManifest?.dataFile)
        dataFileName = latestManifest.dataFile

      // 3. 上传 data.zip（10~50%）
      let dataSize = lastUploadedDataSize.value
      if (dataChanged || dataFileName === `data-${label}.zip`) {
        progressText.value = '打包数据...'
        const dataBlob = await packDataZip(lsData, appData, spriteData, pluginKVData)
        dataSize = dataBlob.size
        if (automatic && expectedDataSize > 32 * 1024 && dataSize < expectedDataSize * 0.25) {
          throw new Error('本地备份数据体积骤降，已暂停自动覆盖。请先确认本地数据完整，再手动备份')
        }
        await uploadFileToBaidu(dataBlob, versionedDataPath(label), 15, 50)
        dataFileName = `data-${label}.zip`
        console.info('[Baidu Sync] 数据已上传')
      }
      else {
        progressPercent.value = 50
      }

      // 4. 上传 models.zip（50~90%，按 hash 去重）
      if (modelsChanged && !modelsAlreadyUploaded) {
        progressText.value = '打包模型文件...'
        const modelsBlob = await packModelsZip(modelFiles)
        await uploadFileToBaidu(modelsBlob, versionedModelsPath(hash8), 65, 90)
        console.info('[Baidu Sync] 模型文件已上传')
      }
      else {
        if (modelsChanged)
          console.info('[Baidu Sync] 模型文件云端已存在，跳过上传')
        progressPercent.value = 90
      }

      // 5. 上传 manifest（90~98%）
      progressText.value = '更新同步清单...'
      const manifestData: BackupVersion = {
        version: 5,
        timestamp: Date.now(),
        label,
        dataHash,
        modelsHash,
        modelsFile: modelsFileName,
        dataFile: dataFileName,
        dataSize,
      }
      const manifestBlob = new Blob([JSON.stringify(manifestData, null, 2)], { type: 'application/json' })
      await uploadFileToBaidu(manifestBlob, versionedManifestPath(label), 95, 98)

      // manifest 上传成功后才更新本地 hash
      if (dataChanged) {
        lastUploadedDataHash.value = dataHash
        lastUploadedDataSize.value = dataSize
      }
      if (modelsChanged)
        lastUploadedModelsHash.value = modelsHash

      // 6. 清理旧版本（98~100%）
      progressText.value = '清理旧备份...'
      const fileListAfter = await listBaiduDirectory()
      await cleanupOldBackups(fileListAfter)

      lastSyncTime.value = Date.now()
      syncStatus.value = 'connected'
      lastError.value = ''
      progressPercent.value = 100
      progressText.value = '备份完成'
      if (!automatic)
        showSuccess('百度网盘备份完成')
      return true
    }
    catch (error) {
      syncStatus.value = 'error'
      lastError.value = formatError(error, '备份')
      progressText.value = '备份失败'
      if (!automatic)
        showError(lastError.value, 6000)
      console.error('[Baidu Sync] 备份失败:', error)
      return false
    }
    finally {
      currentOperation.value = null
    }
  }

  /**
   * 从百度网盘恢复数据
   * v5：按 label 选择版本；向后兼容 v4/v3/v2/v1 旧格式
   * 恢复前创建本地快照，失败时自动回滚
   */
  async function restoreFromBaidu(label?: string): Promise<boolean> {
    if (currentOperation.value)
      return false
    if (!isConfigured.value) {
      const msg = needsReauthorization.value ? '百度网盘授权已失效，请重新授权' : '未授权百度网盘，请先登录授权'
      lastError.value = formatError(msg, '恢复')
      syncStatus.value = 'error'
      return false
    }

    currentOperation.value = 'restore'
    try {
      syncStatus.value = 'syncing'
      progressPercent.value = 0
      progressText.value = '开始恢复...'
      lastError.value = ''

      // 1. 创建本地快照（用于失败回滚）
      progressText.value = '创建本地快照...'
      const snapshot = await createLocalSnapshot()
      progressPercent.value = 5

      try {
        const fileList = await listBaiduDirectory()
        progressPercent.value = 8

        // 2. 确定目标版本
        const manifestFiles = fileList
          .filter(f => f.server_filename.startsWith('manifest-') && f.server_filename.endsWith('.json'))
          .sort((a, b) => b.server_filename.localeCompare(a.server_filename))

        const targetLabel = label || (manifestFiles.length > 0
          ? (await downloadJsonFile(manifestFiles[0].fs_id)).label
          : null)

        if (targetLabel) {
          // ===== v5 新格式恢复 =====
          // 先下载 manifest 获取实际 dataFile 名（data 未变更时可能复用旧版本的文件）
          const manifestFile = fileList.find(f => f.server_filename === `manifest-${targetLabel}.json`)
          let cloudModelsHash = ''
          let cloudModelsFile = ''
          let dataFileName = `data-${targetLabel}.zip`
          if (manifestFile) {
            const manifest = await downloadJsonFile(manifestFile.fs_id)
            cloudModelsHash = manifest.modelsHash || ''
            cloudModelsFile = manifest.modelsFile || ''
            if (manifest.dataFile)
              dataFileName = manifest.dataFile
          }

          const dataFile = fileList.find(f => f.server_filename === dataFileName)
          if (!dataFile)
            throw new Error(`找不到备份文件: ${dataFileName}`)

          // 下载并恢复数据
          progressText.value = '下载数据备份...'
          const dataBlob = await downloadFileFromBaidu(dataFile.fs_id, dataFile.size, 10, 45)
          progressText.value = '恢复数据...'
          const dataZip = await JSZip.loadAsync(dataBlob)
          await unpackDataZip(dataZip)
          progressPercent.value = 50

          // 按需下载模型
          if (cloudModelsFile) {
            const modelsFile = fileList.find(f => f.server_filename === cloudModelsFile)
            if (modelsFile) {
              const localModels = await collectModelFiles()
              const localModelsHash = await computeModelsHash(localModels)
              if (localModelsHash !== cloudModelsHash) {
                progressText.value = '下载模型备份...'
                const modelsBlob = await downloadFileFromBaidu(modelsFile.fs_id, modelsFile.size, 50, 85)
                progressText.value = '恢复模型文件...'
                const modelsZip = await JSZip.loadAsync(modelsBlob)
                await unpackModelsZip(modelsZip)
                lastUploadedModelsHash.value = cloudModelsHash
              }
              else {
                console.info('[Baidu Sync] 本地模型与云端一致，跳过下载')
              }
            }
          }
        }
        else {
          // ===== 旧格式回退（v4/v3/v2/v1）=====
          const dataFile = fileList.find(f => f.server_filename === 'meowchat-data.zip')
          const modelsFile = fileList.find(f => f.server_filename === 'meowchat-models.zip')
          const legacyZipFile = fileList.find(f => f.server_filename === 'meowchat-backup.zip')
          const legacyJsonFile = fileList.find(f => f.server_filename === 'meowchat-data.json')

          if (dataFile) {
            const cloudManifest = await downloadManifest(fileList)
            const effectiveModelsHash = cloudManifest?.modelsHash || ''
            progressText.value = '下载数据备份...'
            const dataBlob = await downloadFileFromBaidu(dataFile.fs_id, dataFile.size, 8, 40)
            const dataZip = await JSZip.loadAsync(dataBlob)
            const { modelsHash: zipModelsHash } = await unpackDataZip(dataZip)
            progressPercent.value = 50
            const finalModelsHash = effectiveModelsHash || zipModelsHash
            if (modelsFile && finalModelsHash) {
              const localModels = await collectModelFiles()
              if (await computeModelsHash(localModels) !== finalModelsHash) {
                const modelsBlob = await downloadFileFromBaidu(modelsFile.fs_id, modelsFile.size, 50, 85)
                const modelsZip = await JSZip.loadAsync(modelsBlob)
                await unpackModelsZip(modelsZip)
                lastUploadedModelsHash.value = finalModelsHash
              }
            }
            else if (modelsFile) {
              const modelsBlob = await downloadFileFromBaidu(modelsFile.fs_id, modelsFile.size, 50, 85)
              const modelsZip = await JSZip.loadAsync(modelsBlob)
              await unpackModelsZip(modelsZip)
            }
          }
          else if (legacyZipFile) {
            const blob = await downloadFileFromBaidu(legacyZipFile.fs_id, legacyZipFile.size, 5, 40)
            await unpackFromZip(blob)
          }
          else if (legacyJsonFile) {
            const blob = await downloadFileFromBaidu(legacyJsonFile.fs_id, legacyJsonFile.size, 5, 40)
            await restoreFromLegacyJson(blob)
          }
          else {
            throw new Error('百度网盘中未找到任何备份文件')
          }
        }

        syncStatus.value = 'connected'
        lastError.value = ''
        progressPercent.value = 100
        progressText.value = '恢复完成，即将刷新页面...'
        setTimeout(() => window.location.reload(), 1500)
        return true
      }
      catch (restoreError) {
        // 恢复失败 → 回滚本地数据
        progressText.value = '恢复失败，正在回滚...'
        console.error('[Baidu Sync] 恢复失败，开始回滚:', restoreError)
        try {
          await restoreLocalSnapshot(snapshot)
          console.info('[Baidu Sync] 回滚成功')
        }
        catch (rollbackError) {
          console.error('[Baidu Sync] 回滚失败:', rollbackError)
        }
        throw restoreError
      }
    }
    catch (error) {
      syncStatus.value = 'error'
      lastError.value = formatError(error, '恢复')
      progressText.value = '恢复失败'
      console.error('[Baidu Sync] 恢复失败:', error)
      return false
    }
    finally {
      currentOperation.value = null
    }
  }

  // ============================================================
  // 自动同步
  // ============================================================

  function stopAutoSync(): void {
    if (_autoSyncTimer) {
      clearInterval(_autoSyncTimer)
      _autoSyncTimer = null
    }
    if (_visibilityHandler) {
      document.removeEventListener('visibilitychange', _visibilityHandler)
      _visibilityHandler = null
    }
  }

  async function tryAutoBackup(): Promise<void> {
    if (isBusy.value)
      return
    if (!isConfigured.value)
      return
    if (document.visibilityState !== 'visible')
      return
    try {
      await backupNow(true)
    }
    catch (err) {
      console.warn('[Baidu Sync] 自动备份失败:', err)
    }
  }

  function startAutoSync(): void {
    stopAutoSync()
    if (!autoSyncEnabled.value || !isConfigured.value)
      return

    _autoSyncTimer = setInterval(() => tryAutoBackup(), syncInterval.value)

    _visibilityHandler = () => {
      if (document.visibilityState !== 'visible')
        return
      if (!autoSyncEnabled.value || !isConfigured.value)
        return
      if (Date.now() - lastSyncTime.value >= syncInterval.value) {
        tryAutoBackup()
      }
    }
    document.addEventListener('visibilitychange', _visibilityHandler)

    console.info(`[Baidu Sync] 自动同步已启动，间隔 ${syncInterval.value / 1000}s`)
  }

  function initAutoSync(): void {
    if (autoSyncEnabled.value && isConfigured.value) {
      if (Date.now() - lastSyncTime.value >= syncInterval.value) {
        tryAutoBackup()
      }
      startAutoSync()
    }

    watch(autoSyncEnabled, (enabled) => {
      if (enabled && isConfigured.value) {
        startAutoSync()
        tryAutoBackup() // 首次开启立即备份，不等第一个 interval
      }
      else if (!enabled) {
        stopAutoSync()
      }
    })

    watch(isConfigured, (configured) => {
      if (configured && autoSyncEnabled.value) {
        startAutoSync()
        if (!lastSyncTime.value || Date.now() - lastSyncTime.value >= syncInterval.value)
          void tryAutoBackup()
      }
      else if (!configured) {
        stopAutoSync()
      }
    })

    watch(syncInterval, () => {
      if (autoSyncEnabled.value && isConfigured.value) {
        startAutoSync()
      }
    })
  }

  onScopeDispose(() => {
    stopAutoSync()
    if (tokenClockTimer !== null)
      window.clearInterval(tokenClockTimer)
    if (typeof document !== 'undefined')
      document.removeEventListener('visibilitychange', tokenVisibilityHandler)
  })

  return {
    // 状态
    accessToken,
    tokenExpireTime,
    autoSyncEnabled,
    syncInterval,
    syncStatus,
    currentOperation,
    isBusy,
    lastError,
    lastSyncTime,
    baiduUserName,
    progressPercent,
    progressText,
    backupVersions,
    isLoadingVersions,
    // 计算属性
    isConfigured,
    isTokenExpired,
    needsReauthorization,
    // 方法
    initializeConnectionStatus,
    startOAuth,
    backupNow,
    restoreFromBaidu,
    listBackupVersions,
    clearCredentials,
    setAutoSyncEnabled,
    setSyncInterval,
    initAutoSync,
    exportToFile,
    importFromFile,
  }
})
